import { useState } from 'react';
import { ABOUT } from '@/data/about';

/**
 * `200+` → the number the counter climbs to, and whatever follows it. Read at
 * render, not at import — `ABOUT` is filled in by the CMS after this module
 * has loaded.
 */
const counter = () => {
    const match = /^\s*(\d+)(.*)$/.exec(ABOUT.volunteers.counterValue);
    const end = match ? Number(match[1]) : 200;
    return { end, start: Math.max(0, end - 29), suffix: match ? match[2].trim() : '+' };
};
import { CountUp, Reveal } from '@components/motion';
import FigmaFrame from './FigmaFrame';
import VolunteerForm from './VolunteerForm';

const STATUE = '/landing/volunteers/statue.webp';
const CARD_WIDE = '/landing/volunteers/card-wide-new.webp';
const CARD_TALL = '/landing/volunteers/card-tall-new.webp';

const LEAD_PARAGRAPH =
    'Podus Contract is a smart legal tool designed to help individuals and businesses create, review, and manage contracts with ease. By combining AI-powered analysis with expert-backed legal standards along side human';

/** The two promo cards, written on the About page; the bundled photographs stand in until one is uploaded. */
const wideCard = () => ({ ...ABOUT.volunteers.wideCard, image: ABOUT.volunteers.wideCard.image || CARD_WIDE });
const tallCard = () => ({ ...ABOUT.volunteers.tallCard, image: ABOUT.volunteers.tallCard.image || CARD_TALL });

function VolunteersDesktop({ onOpen }: { onOpen: () => void }) {
    return (
        <FigmaFrame designWidth={1920} designHeight={812} className="w-full">
            <div className="relative h-[812px] w-[1920px] bg-[#1b1b1b] font-primary text-white">
                {/* Decorative Lady Justice statue */}
                <div className="absolute left-[625px] top-[124px] h-[639px] w-[518px] overflow-hidden opacity-[0.06]">
                    <img src={STATUE} alt="" className="absolute left-0 top-0 h-full w-full object-contain" />
                </div>

                {/* Left text group */}
                <Reveal
                    as="div"
                    once
                    variant="fade-right"
                    className="absolute left-[134px] top-1/2 h-[424px] w-[662.459px] -translate-y-1/2"
                >
                    <div className="absolute left-0 top-[103px] -translate-y-1/2 whitespace-nowrap text-[96px] font-extrabold leading-[103px]">
                        <p className="mb-0 font-normal text-[#6c6c6c]">{ABOUT.volunteers.titleLead}</p>
                        <p>
                            <span>{ABOUT.volunteers.titleBold}</span>
                            <span className="text-[#f60]">?</span>
                        </p>
                    </div>
                    <p className="absolute left-0 top-[251px] w-[662.459px] text-[18px] font-normal leading-[28px] text-white">
                        {LEAD_PARAGRAPH}
                    </p>
                    <button
                        type="button"
                        onClick={onOpen}
                        className="absolute left-[9px] top-[380px] flex w-[216px] items-center justify-center rounded-[10px] bg-[#f60] px-[20px] py-[10px] text-[16px] font-bold leading-[24px] text-white transition-transform duration-300 hover:scale-[1.03]"
                    >
                        Get Started Here
                    </button>
                </Reveal>

                {/* 200+ stat */}
                <Reveal
                    as="div"
                    once
                    variant="fade-up"
                    delay={220}
                    className="absolute left-[1113px] top-[535px] flex h-[106.178px] w-[311.156px] flex-col justify-center gap-[27px] whitespace-nowrap"
                >
                    <p className="text-[84px] font-bold leading-none tracking-[-2.52px]">
                        <CountUp end={counter().end} start={counter().start} />
                        <span className="text-[#f37b23]">{counter().suffix}</span>
                    </p>
                    <p className="text-[32px] font-medium leading-[1.7] text-[#9a9a9a]">
                        {ABOUT.volunteers.counterLabel}
                    </p>
                </Reveal>

                {/* Wide card */}
                <Reveal
                    as="div"
                    once
                    variant="fade-left"
                    className="absolute left-[1086px] top-[114px] h-[330px] w-[392px] overflow-hidden rounded-[30px] bg-[#f5f5f5]"
                >
                    <img
                        src={wideCard().image}
                        alt={ABOUT.volunteers.wideCard.image ? wideCard().imageAlt : ''}
                        className="pointer-events-none absolute inset-0 size-full object-cover"
                    />
                    <p className="absolute left-[calc(50%-169px)] top-[178px] w-[230px] text-[28px] font-bold leading-[29px] tracking-[-0.5px] text-white">
                        {wideCard().title}
                    </p>
                    <p className="absolute left-[calc(50%-169px)] top-[275px] w-[333px] -translate-y-1/2 text-[16px] font-normal leading-[24px] text-white">
                        {wideCard().body}
                    </p>
                </Reveal>

                {/* Tall card */}
                <Reveal
                    as="div"
                    once
                    variant="fade-left"
                    delay={120}
                    className="absolute left-[1520px] top-[114px] h-[602px] w-[355px] overflow-hidden rounded-[30px] bg-[#f5f5f5]"
                >
                    <img
                        src={tallCard().image}
                        alt={ABOUT.volunteers.tallCard.image ? tallCard().imageAlt : ''}
                        className="pointer-events-none absolute inset-0 size-full object-cover"
                    />
                    <p className="absolute left-[calc(50%-163.5px)] top-[448px] w-[302px] text-[28px] font-bold leading-[29px] tracking-[-0.5px] text-white">
                        {tallCard().title}
                    </p>
                    <p className="absolute left-[calc(50%-163.5px)] top-[545px] w-[337px] -translate-y-1/2 text-[16px] font-normal leading-[24px] text-white">
                        {tallCard().body}
                    </p>
                </Reveal>
            </div>
        </FigmaFrame>
    );
}

function VolunteersMobile({ onOpen }: { onOpen: () => void }) {
    return (
        <section className="relative overflow-hidden bg-[#1b1b1b] font-primary text-white">
            {/* Decorative Lady Justice statue */}
            <img
                src={STATUE}
                alt=""
                className="pointer-events-none absolute right-0 top-[94px] h-[408px] w-auto object-contain opacity-[0.06]"
            />

            <div className="relative px-5 py-[94px]">
                <h2 className="text-[40px] font-extrabold leading-[40px]">
                    <span className="block font-normal text-[#6c6c6c]">{ABOUT.volunteers.titleLead}</span>
                    <span className="block">
                        {ABOUT.volunteers.titleBold}
                        <span className="text-[#f60]">?</span>
                    </span>
                </h2>

                <p className="mt-[28px] max-w-[331px] text-[14px] font-normal leading-[24px] text-white">
                    {LEAD_PARAGRAPH}
                </p>

                <button
                    type="button"
                    onClick={onOpen}
                    className="mt-[24px] flex w-[216px] items-center justify-center rounded-[10px] bg-[#f60] px-5 py-[10px] text-[16px] font-bold leading-[24px] text-white"
                >
                    Get Started Here
                </button>
            </div>
        </section>
    );
}

export default function VolunteersCTA() {
    // One panel serves both breakpoints, so the open state lives here.
    const [formOpen, setFormOpen] = useState(false);
    const open = () => setFormOpen(true);

    return (
        <>
            <div className="hidden lg:block">
                <VolunteersDesktop onOpen={open} />
            </div>
            <div className="lg:hidden">
                <VolunteersMobile onOpen={open} />
            </div>

            <VolunteerForm open={formOpen} onClose={() => setFormOpen(false)} />
        </>
    );
}
